import { format } from 'date-fns';

export interface ActivityLogEntry {
  id: string;
  family_id: string;
  user_id: string;
  user_email?: string;
  user_display_email?: string;
  action: string;
  details: string;
  created_at: string;
  metadata?: Record<string, any>;
  entity_name?: string;
  entity_type?: string;
  entity_id?: string;
  changes?: Record<string, any>;
}

export type ActivityAction = 
  | 'calendar_entry_created'
  | 'calendar_entry_updated'
  | 'calendar_entry_deleted'
  | 'event_created'
  | 'event_updated'
  | 'event_deleted'
  | 'note_created'
  | 'note_updated'
  | 'note_deleted'
  | 'expense_created'
  | 'expense_updated'
  | 'expense_deleted'
  | 'member_added'
  | 'member_removed'
  | 'member_role_changed'
  | 'child_added'
  | 'child_updated'
  | 'child_removed'
  | 'family_settings_updated'
  | 'invitation_sent'
  | 'invitation_accepted'
  | 'invitation_rejected'
  | 'system_update';

export function formatActivityAction(action: string): string {
  const actionMap: Record<string, string> = {
    'calendar_entry_created': 'Calendar entry added',
    'calendar_entry_updated': 'Calendar entry updated',
    'calendar_entry_deleted': 'Calendar entry deleted',
    'event_created': 'Event added',
    'event_updated': 'Event updated',
    'event_deleted': 'Event deleted',
    'note_created': 'Note added',
    'note_updated': 'Note updated',
    'note_deleted': 'Note deleted',
    'expense_created': 'Expense added',
    'expense_updated': 'Expense updated',
    'expense_deleted': 'Expense deleted',
    'member_added': 'Family member added',
    'member_removed': 'Family member removed',
    'member_role_changed': 'Family member role changed',
    'child_added': 'Child added',
    'child_updated': 'Child data updated',
    'child_removed': 'Child removed',
    'family_settings_updated': 'Family settings updated',
    'invitation_sent': 'Invitation sent',
    'invitation_accepted': 'Invitation accepted',
    'invitation_rejected': 'Invitation rejected',
    'system_update': 'System update'
  };

  return actionMap[action] || action;
}

export function formatActivityDetails(entry: ActivityLogEntry): string {
  // Debug in DEV: check what data we have (no PII in production)
  if (import.meta && !import.meta.env?.PROD) {
    // Note: do not log entire objects in production
  }
  
  if (!entry.metadata) {
    console.log('⚠️ Missing metadata, using details:', entry.details);
    return entry.details;
  }

  switch (entry.action) {
    case 'calendar_entry_created':
      const dateFromEntity = extractDateFromEntityName(entry.entity_name);
      return `Added calendar entry for child for ${formatDate(dateFromEntity)}`;
    
    case 'calendar_entry_updated':
      const dateFromEntityUpd = extractDateFromEntityName(entry.entity_name);
      return `Changed calendar entry for child for ${formatDate(dateFromEntityUpd)}`;
    
    case 'calendar_entry_deleted':
      const dateFromEntityDel = extractDateFromEntityName(entry.entity_name);
      return `Removed calendar entry for child from ${formatDate(dateFromEntityDel)}`;
    
    case 'member_added':
      return `Added user ${entry.metadata.email || ''} as ${formatRole(entry.metadata.role)}`;
    
    case 'member_removed':
      return `Removed user ${entry.metadata.email || ''} (${formatRole(entry.metadata.role)})`;
    
    case 'member_role_changed':
      return `Changed role of user ${entry.metadata.email || ''} from ${formatRole(entry.metadata.old_role)} to ${formatRole(entry.metadata.new_role)}`;
    
    case 'child_added':
      return `Added child: ${entry.metadata.name || ''}`;
    
    case 'child_updated':
      return `Updated child data: ${entry.metadata.name || ''}`;
    
    case 'child_removed':
      return `Removed child: ${entry.metadata.name || ''}`;
    
    case 'family_settings_updated':
      const changes = [];
      if (entry.metadata.name_changed) changes.push('family name');
      if (entry.metadata.home_a_changed) changes.push('Home A name');
      if (entry.metadata.home_b_changed) changes.push('Home B name');
      return `Settings changed: ${changes.join(', ')}`;
    
    case 'invitation_sent':
      return `Invitation sent to ${entry.metadata.email || ''} (role: ${formatRole(entry.metadata.role)})`;
    
    case 'invitation_accepted':
      return `Invitation for ${entry.metadata.email || ''} was accepted`;
    
    case 'invitation_rejected':
      return `Invitation for ${entry.metadata.email || ''} was rejected`;
    
    case 'system_update':
      return `System update: ${entry.details}`;
    
    default:
      return entry.details;
  }
}

function formatDate(dateString?: string): string {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    return format(date, 'dd MMM yyyy');
  } catch (e) {
    return dateString;
  }
}

function extractDateFromEntityName(entityName?: string): string {
  if (!entityName) return '';
  
  // entity_name format: "2025-07-31 - day"
  const match = entityName.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : '';
}

function formatRole(role?: string): string {
  if (!role) return '';
  
  const roleMap: Record<string, string> = {
    'parent_a': 'Parent (Admin)',
    'parent_b': 'Parent',
    'caregiver': 'Caregiver',
    'child_viewer': 'Viewer'
  };

  return roleMap[role] || role;
}
